/**
 * Reads the JWT `exp` claim so the token store can replace the bearer *before*
 * it expires, instead of waiting for the API to reject it.
 *
 * The SDK ships the same decoder but doesn't export it. The signature is
 * deliberately not verified: the miniapp can't hold the signing key, and `exp`
 * only decides whether a refresh round-trip is worth spending.
 */

/**
 * How long before `exp` a token counts as stale. Also passed to the SDK's
 * `getValidToken` as its `skew`, so the store and the SDK agree on "stale".
 */
export const TOKEN_REFRESH_SKEW_MS = 60_000;

const JWT_SEGMENT_COUNT = 3;
const BASE64_BLOCK_SIZE = 4;
const SECONDS_TO_MS = 1000;

/** `exp` as a millisecond epoch, or `null` when the token isn't a decodable JWT. */
export const getJwtExpiryMs = (token: string | null | undefined): number | null => {
  const segments = token?.split('.') ?? [];
  if (segments.length !== JWT_SEGMENT_COUNT) return null;

  try {
    const base64 = segments[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / BASE64_BLOCK_SIZE) * BASE64_BLOCK_SIZE, '=');
    const { exp } = JSON.parse(atob(padded)) as { exp?: unknown };

    return typeof exp === 'number' ? exp * SECONDS_TO_MS : null;
  } catch {
    return null;
  }
};

/**
 * Whether `token` expires within `skewMs`. A token whose `exp` can't be read
 * (opaque, malformed or empty) counts as usable, so it never blocks a request
 * or triggers a refresh; the reactive 401 retry still covers it.
 */
export const isTokenStale = (token: string | null | undefined, skewMs: number): boolean => {
  const expiresAt = getJwtExpiryMs(token);

  return expiresAt !== null && expiresAt - Date.now() <= skewMs;
};
